/*
 * pcard-rowfit.js
 *
 * Equalizes the height of every `.product-card__title-row` container PER VISUAL
 * ROW inside a product grid, so cards in the same row keep their price / swatch
 * / button rows aligned even when one product title wraps to two lines while its
 * neighbours stay on one.
 *
 * This is the multi-row sibling of pcard-title-fit.js: that file equalizes a
 * single-row carousel uniformly across the whole root; a collection grid has
 * MANY rows, so equalizing every card to the global tallest title would over-pad
 * short rows. Here we cluster cards by their title-row's vertical position
 * (offsetTop) and equalize within each cluster — one max height per row.
 *
 * Why the title-row CONTAINER (not the inner `.product-card__title`): the
 * container holds the title + BV rating and carries the `tw-min-h-[48px]
 * md:tw-min-h-[60px]` floor. Pinning the container to the row max pushes the
 * price / swatches / ATC that follow it down to the same Y on every card in the
 * row — which is the alignment the merchant sees. The reset step neutralizes
 * that Tailwind floor (inline min-height:0) so a row of all-one-line titles is
 * NOT forced to reserve 2-line space — the row height is purely the tallest
 * title in that visual row.
 *
 * Scope is per-root: each grid opts in with `data-pcard-rowfit` on the element
 * that wraps its cards (e.g. <grid-list id="ProductsList">). Multiple grids on
 * one page are handled independently.
 *
 * Used by:
 *  - sections/main-collection-product-grid.liquid (standard layout → card-product-v2)
 *
 * Re-run triggers mirror pcard-title-fit.js: DOMContentLoaded, window 'load',
 * debounced 'resize', document.fonts.ready, 'pageshow' (bfcache), the theme's
 * 'collection:rerendered' (facet filter / sort / load-more re-render) and
 * 'shopify:section:load' (theme editor), plus a debounced document-level
 * MutationObserver for AJAX-replaced grids and lazily revealed cards.
 *
 * ES5-compatible (var / function) to match the other v2 card assets.
 */
(function () {
  if (window.__pcardRowFitInit) return;
  window.__pcardRowFitInit = true;

  var ROW_SELECTOR = '.product-card__title-row';
  var ROOT_SELECTOR = '[data-pcard-rowfit]';
  var RESIZE_DEBOUNCE_MS = 150;
  // Two title-rows whose offsetTop differ by no more than this are treated as
  // the same visual row. Intra-row variance is sub-pixel; inter-row gaps are a
  // whole card tall, so a few px of slack cleanly separates rows.
  var ROW_TOLERANCE_PX = 4;

  // Equalize title-rows within a single root, grouped by visual row.
  function equalizeRoot(root) {
    if (!root) return;
    var rows = root.querySelectorAll(ROW_SELECTOR);
    if (!rows.length) return; // guard: nothing to fit yet

    var i;

    // 1) Reset so wrapping is natural and resizing DOWN works (no stale heights).
    //    Also neutralize the Tailwind min-height floor (tw-min-h-[48px] /
    //    md:tw-min-h-[60px]) with an inline min-height:0 so a row whose titles
    //    all fit on one line collapses to one line instead of reserving 2-line
    //    space. Inline style beats the utility class; the class stays in the
    //    markup as the no-JS fallback.
    for (i = 0; i < rows.length; i++) {
      rows[i].style.height = 'auto';
      rows[i].style.minHeight = '0px';
    }

    // 2) Measure visible rows only; a hidden card (display:none) reports
    //    offsetParent null + 0 height. visibility:hidden cards (the grid reveal
    //    start state) keep layout, so they measure correctly.
    var items = [];
    for (i = 0; i < rows.length; i++) {
      var el = rows[i];
      var h = el.offsetHeight;
      if (h <= 0) continue;
      items.push({ el: el, top: Math.round(el.offsetTop), h: h });
    }
    if (!items.length) return; // every card hidden — try again later

    // 3) Cluster by offsetTop (ascending) and apply the per-cluster max height.
    items.sort(function (a, b) { return a.top - b.top; });

    var group = [];
    var groupTop = items[0].top;
    var max = 0;

    function flushGroup() {
      if (!group.length) return;
      var maxPx = max + 'px';
      for (var k = 0; k < group.length; k++) {
        group[k].style.height = maxPx;
      }
      group = [];
      max = 0;
    }

    for (i = 0; i < items.length; i++) {
      if (items[i].top - groupTop > ROW_TOLERANCE_PX) {
        flushGroup();
        groupTop = items[i].top;
      }
      group.push(items[i].el);
      if (items[i].h > max) max = items[i].h;
    }
    flushGroup();
  }

  // Equalize every root on the page (or only those inside `scope`, if given).
  function equalizeAll(scope) {
    var roots = (scope || document).querySelectorAll(ROOT_SELECTOR);
    for (var i = 0; i < roots.length; i++) {
      equalizeRoot(roots[i]);
    }
    if (scope && scope.nodeType === 1 && scope.matches && scope.matches(ROOT_SELECTOR)) {
      equalizeRoot(scope);
    }
  }

  // rAF wrapper so layout is settled (fonts/images applied) before measuring.
  function scheduleAll(scope) {
    requestAnimationFrame(function () {
      equalizeAll(scope);
    });
  }

  // Late layout (lazy images, font swap that re-wraps titles, grid reveal
  // animation) settles at unpredictable times and some changes don't trip the
  // MutationObserver. A short burst of passes guarantees a settled measurement;
  // re-applying the same max is a no-op so the extra passes are harmless.
  function pollEqualize() {
    var delays = [0, 250, 600, 1200, 2000, 3500, 5000];
    for (var i = 0; i < delays.length; i++) {
      setTimeout(function () {
        scheduleAll();
      }, delays[i]);
    }
  }

  // --- Debounced resize -----------------------------------------------------
  var resizeTimer = null;
  function onResize() {
    if (resizeTimer) clearTimeout(resizeTimer);
    resizeTimer = setTimeout(function () {
      scheduleAll();
    }, RESIZE_DEBOUNCE_MS);
  }

  // --- Global MutationObserver ----------------------------------------------
  // One document-level observer so we also catch grids replaced AFTER init —
  // facet filtering / sorting / load-more swap the grid's innerHTML. childList +
  // subtree only (we never observe attributes), so our own style.height writes
  // don't retrigger it — no feedback loop. Debounced (not rAF): an AJAX
  // re-render is a burst of mutations; firing once ~250ms after the last one
  // measures a settled layout.
  var observeTimer = null;
  function onMutations() {
    if (observeTimer) clearTimeout(observeTimer);
    observeTimer = setTimeout(function () {
      observeTimer = null;
      scheduleAll();
    }, 250);
  }

  function observeDocument() {
    if (typeof MutationObserver === 'undefined') return;
    if (window.__pcardRowFitObserver) return; // bind once
    var target = document.body || document.documentElement;
    if (!target) return;
    var observer = new MutationObserver(onMutations);
    observer.observe(target, { childList: true, subtree: true });
    window.__pcardRowFitObserver = observer;
  }

  // --- Init -----------------------------------------------------------------
  function init() {
    scheduleAll();
    observeDocument();
    pollEqualize();
  }

  if (document.readyState !== 'loading') {
    init();
  } else {
    document.addEventListener('DOMContentLoaded', init);
  }

  window.addEventListener('load', function () {
    scheduleAll();
    observeDocument();
    pollEqualize();
  });

  // Font load changes line wrapping → title heights change.
  if (document.fonts && document.fonts.ready && typeof document.fonts.ready.then === 'function') {
    document.fonts.ready.then(function () {
      scheduleAll();
    });
  }

  window.addEventListener('resize', onResize);

  // bfcache restore (back/forward) — layout may differ from the cached pass.
  window.addEventListener('pageshow', function () {
    scheduleAll();
  });

  // Facet filter / sort / load-more AJAX re-render.
  document.addEventListener('collection:rerendered', function () {
    scheduleAll();
    observeDocument();
  });

  // Theme editor section re-render.
  document.addEventListener('shopify:section:load', function (event) {
    scheduleAll(event.target || undefined);
    observeDocument();
  });
})();
